import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  PRIVACY_CONSENT_VERSION,
  PRIVACY_CONSENT_STORAGE_KEY,
  PrivacyConsentPreferences,
  clearAnalyticsSessionStorage,
  clearMapboxStorage,
  isGlobalPrivacyControlEnabled,
  readPrivacyConsent,
  writePrivacyConsent,
} from "./consentStorage";

type OptionalPrivacyChoices = Pick<
  PrivacyConsentPreferences,
  "functional" | "analytics" | "advertising"
>;

type PrivacyConsentContextValue = {
  preferences: PrivacyConsentPreferences | null;
  hasDecision: boolean;
  functionalAllowed: boolean;
  analyticsAllowed: boolean;
  advertisingAllowed: boolean;
  gpcEnabled: boolean;
  isPreferencesOpen: boolean;
  acceptAvailable: () => void;
  rejectOptional: () => void;
  saveChoices: (choices: OptionalPrivacyChoices) => void;
  allowFunctional: () => void;
  openPreferences: () => void;
  closePreferences: () => void;
};

const PrivacyConsentContext = createContext<PrivacyConsentContextValue | null>(
  null
);

const createPreferences = (
  choices: OptionalPrivacyChoices,
  gpcEnabled: boolean
): PrivacyConsentPreferences => ({
  version: PRIVACY_CONSENT_VERSION,
  necessary: true,
  functional: choices.functional,
  analytics: choices.analytics,
  advertising: gpcEnabled ? false : choices.advertising,
  updatedAt: new Date().toISOString(),
  source: gpcEnabled ? "gpc" : "user",
  gpc: gpcEnabled,
});

export const PrivacyConsentProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [gpcEnabled] = useState(isGlobalPrivacyControlEnabled);
  const [preferences, setPreferences] =
    useState<PrivacyConsentPreferences | null>(() => {
      const storedPreferences = readPrivacyConsent();
      if (!storedPreferences) {
        return null;
      }

      if (gpcEnabled) {
        return {
          ...storedPreferences,
          advertising: false,
          gpc: true,
          source: "gpc",
          updatedAt: new Date().toISOString(),
        };
      }

      return storedPreferences;
    });
  const [isPreferencesOpen, setIsPreferencesOpen] = useState(false);

  useEffect(() => {
    if (gpcEnabled && preferences) {
      writePrivacyConsent(preferences);
    }
  }, [gpcEnabled, preferences]);

  useEffect(() => {
    const handleStorageChange = (event: StorageEvent) => {
      if (event.key !== PRIVACY_CONSENT_STORAGE_KEY) {
        return;
      }

      const storedPreferences = readPrivacyConsent();
      const nextPreferences =
        storedPreferences && gpcEnabled
          ? {
              ...storedPreferences,
              advertising: false,
              gpc: true,
              source: "gpc" as const,
            }
          : storedPreferences;

      setPreferences(nextPreferences);
      setIsPreferencesOpen(false);

      if (!nextPreferences?.functional) {
        clearMapboxStorage();
        window.requestAnimationFrame(clearMapboxStorage);
      }
      if (!nextPreferences?.analytics) {
        clearAnalyticsSessionStorage();
      }
    };

    window.addEventListener("storage", handleStorageChange);
    return () => window.removeEventListener("storage", handleStorageChange);
  }, [gpcEnabled]);

  const persistChoices = useCallback(
    (choices: OptionalPrivacyChoices) => {
      const nextPreferences = createPreferences(choices, gpcEnabled);
      setPreferences(nextPreferences);
      writePrivacyConsent(nextPreferences);
      setIsPreferencesOpen(false);

      if (!nextPreferences.functional) {
        clearMapboxStorage();
        // Let consent-gated map components unmount before a second cleanup;
        // Mapbox may persist its final SDK state during teardown.
        if (typeof window !== "undefined") {
          window.requestAnimationFrame(clearMapboxStorage);
        }
      }

      if (!nextPreferences.analytics && typeof window !== "undefined") {
        clearAnalyticsSessionStorage();
      }

      if (typeof window !== "undefined") {
        window.dispatchEvent(
          new CustomEvent("rauxa:privacy-consent-changed", {
            detail: nextPreferences,
          })
        );
      }
    },
    [gpcEnabled]
  );

  const acceptAvailable = useCallback(() => {
    persistChoices({
      functional: true,
      analytics: true,
      advertising: false,
    });
  }, [persistChoices]);

  const rejectOptional = useCallback(() => {
    persistChoices({
      functional: false,
      analytics: false,
      advertising: false,
    });
  }, [persistChoices]);

  const allowFunctional = useCallback(() => {
    persistChoices({
      functional: true,
      analytics: preferences?.analytics || false,
      advertising: preferences?.advertising || false,
    });
  }, [persistChoices, preferences]);

  const contextValue = useMemo<PrivacyConsentContextValue>(
    () => ({
      preferences,
      hasDecision: Boolean(preferences),
      functionalAllowed: preferences?.functional === true,
      analyticsAllowed: preferences?.analytics === true,
      advertisingAllowed: preferences?.advertising === true && !gpcEnabled,
      gpcEnabled,
      isPreferencesOpen,
      acceptAvailable,
      rejectOptional,
      saveChoices: persistChoices,
      allowFunctional,
      openPreferences: () => setIsPreferencesOpen(true),
      closePreferences: () => setIsPreferencesOpen(false),
    }),
    [
      preferences,
      gpcEnabled,
      isPreferencesOpen,
      acceptAvailable,
      rejectOptional,
      persistChoices,
      allowFunctional,
    ]
  );

  return (
    <PrivacyConsentContext.Provider value={contextValue}>
      {children}
    </PrivacyConsentContext.Provider>
  );
};

export const usePrivacyConsent = (): PrivacyConsentContextValue => {
  const context = useContext(PrivacyConsentContext);
  if (!context) {
    throw new Error(
      "usePrivacyConsent must be used within PrivacyConsentProvider."
    );
  }

  return context;
};
